import React, { useState, useEffect, useRef } from 'react';
import { Box } from '@mui/material';
import { useReducedMotion } from '../../hooks/useReducedMotion';

/**
 * Plays Discord-style profile effect overlays (APNG layers with timing).
 * Browsers render APNG natively - no canvas extraction needed.
 */
const ProfileEffectOverlay = ({
  effect,
  width = 450,
  height = 880,
  scale = 1,
  fillContainer = false,
  onComplete,
  zIndex = 1000,
}) => {
  const prefersReduced = useReducedMotion();
  const [visibleLayers, setVisibleLayers] = useState({});
  const [loaded, setLoaded] = useState({});
  const [allLoaded, setAllLoaded] = useState(false);
  const completedRef = useRef(false);
  const timersRef = useRef([]);
  const instanceId = useRef(Date.now());

  // Reset completed state when effect changes (new animation)
  useEffect(() => {
    completedRef.current = false;
    instanceId.current = Date.now();
    setVisibleLayers({});
    setLoaded({});
    setAllLoaded(false);
  }, [effect]);

  // Preload images
  useEffect(() => {
    if (!effect?.effects) return;

    let cancelled = false;

    Promise.all(
      effect.effects.map((layer, idx) => {
        return new Promise((resolve) => {
          const img = new Image();
          img.onload = () => {
            if (!cancelled) {
              setLoaded(prev => ({ ...prev, [idx]: true }));
            }
            resolve();
          };
          img.onerror = () => resolve();
          img.src = `${layer.src}?t=${instanceId.current}`;
        });
      })
    ).then(() => {
      if (!cancelled) setAllLoaded(true);
    });

    return () => { cancelled = true; };
  }, [effect]);

  // 3s timeout fallback for slow connections
  useEffect(() => {
    if (allLoaded) return;
    const timeout = setTimeout(() => {
      setAllLoaded(true);
    }, 3000);
    return () => clearTimeout(timeout);
  }, [allLoaded]);

  // Start animation once loaded
  useEffect(() => {
    if (!effect?.effects || !allLoaded || completedRef.current) return;

    const timers = [];

    effect.effects.forEach((layer, idx) => {
      if (layer.loop) {
        if (layer.start > 0) {
          const t = setTimeout(() => {
            setVisibleLayers(prev => ({ ...prev, [idx]: true }));
          }, layer.start);
          timers.push(t);
        } else {
          setVisibleLayers(prev => ({ ...prev, [idx]: true }));
        }
      } else {
        const showTimer = setTimeout(() => {
          setVisibleLayers(prev => ({ ...prev, [idx]: true }));
        }, layer.start || 0);

        const hideTimer = setTimeout(() => {
          setVisibleLayers(prev => ({ ...prev, [idx]: false }));
        }, (layer.start || 0) + (layer.duration || 2000));

        timers.push(showTimer, hideTimer);
      }
    });

    // Fire onComplete when all non-looping layers finish AND looping layers have started
    const nonLoopEndTime = effect.effects
      .filter(l => !l.loop)
      .reduce((max, l) => Math.max(max, (l.start || 0) + (l.duration || 2000)), 0);

    // Also wait for looping layers to start + play at least one cycle
    const loopLayerMinTime = effect.effects
      .filter(l => l.loop)
      .reduce((max, l) => Math.max(max, (l.start || 0) + (l.duration || 2000)), 0);

    const maxEndTime = Math.max(nonLoopEndTime, loopLayerMinTime);

    if (maxEndTime > 0) {
      const completeTimer = setTimeout(() => {
        if (!completedRef.current) {
          completedRef.current = true;
          onComplete?.();
        }
      }, maxEndTime + 200);
      timers.push(completeTimer);
    }

    timersRef.current = timers;

    return () => {
      timers.forEach(clearTimeout);
    };
  }, [effect, allLoaded, onComplete]);

  // Reduced motion: static image only
  if (prefersReduced && effect?.reducedMotionSrc) {
    return (
      <Box
        component="img"
        src={effect.reducedMotionSrc}
        alt=""
        sx={{
          position: 'absolute',
          inset: 0,
          width: '100%',
          height: '100%',
          objectFit: 'contain',
          zIndex,
          pointerEvents: 'none',
          opacity: 0.8,
        }}
      />
    );
  }

  if (!allLoaded) return null;

  // Fill container mode - stretch to cover entire area
  if (fillContainer) {
    return (
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          overflow: 'hidden',
          zIndex,
          pointerEvents: 'none',
        }}
      >
        {effect?.effects?.map((layer, idx) => (
          visibleLayers[idx] && (
            <Box
              key={idx}
              component="img"
              src={`${layer.src}?t=${instanceId.current}`}
              alt=""
              sx={{
                position: 'absolute',
                inset: 0,
                width: '100%',
                height: 'auto',
                objectFit: 'initial',
                objectPosition: 'top',
                zIndex: layer.zIndex || idx,
                pointerEvents: 'none',
              }}
            />
          )
        ))}
      </Box>
    );
  }

  return (
    <Box
      sx={{
        position: 'absolute',
        inset: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden',
        zIndex,
        pointerEvents: 'none',
      }}
    >
      <Box
        sx={{
          position: 'relative',
          width: width * scale,
          height: height * scale,
        }}
      >
        {effect?.effects?.map((layer, idx) => (
          visibleLayers[idx] && (
            <Box
              key={idx}
              component="img"
              src={`${layer.src}?t=${instanceId.current}`}
              alt=""
              sx={{
                position: 'absolute',
                top: (layer.position?.y || 0) * scale,
                left: (layer.position?.x || 0) * scale,
                width: (layer.width || width) * scale,
                height: (layer.height || height) * scale,
                zIndex: layer.zIndex || idx,
                pointerEvents: 'none',
              }}
            />
          )
        ))}
      </Box>
    </Box>
  );
};

export default ProfileEffectOverlay;
